/**
 * Maximum length of InteractionEvent.Detail and InteractionEvent.PreviousDetail — mirrors the
 * nvarchar(4000) columns set by 20260814161000_AddInteractionEventIdentityAndHistory.
 *
 * Keep this in step with the column. The two drifting apart means the client clips to a width the
 * column no longer has (silently losing detail it could have kept) or, worse, stops clipping
 * before the column's real limit and lets SQL Server reject the insert — which the client, firing
 * tracking and forgetting it, would never notice.
 */
export const DETAIL_MAX_LENGTH = 4000;

/**
 * Matches a segment's leading "marker: " label, e.g. "; tags: a,b" -> "tags: ".
 * Kept deliberately narrow (letters and spaces only) so a card name containing a colon isn't
 * mistaken for a marker.
 */
const MARKER = /^\s*[A-Za-z][A-Za-z ]*:\s/;

/**
 * Clips a tracking detail string to the column width WITHOUT destroying its structure.
 *
 * This cannot be a plain `slice(0, max)`. The Nova event views parse these details by locating
 * markers — "; type: ", "; tags: ", "; seconds: " — and several of those sit at the END of the
 * string. Chopping the tail therefore removes exactly the fields the views exist to read, turning
 * a too-long event into a silently mis-parsed one.
 *
 * Instead the longest segment is shortened repeatedly until the whole string fits, so every marker
 * survives and only the bulkiest value loses characters. That bulk is nearly always a joined list
 * (card names, tag names) where losing the tail of the list is the cheapest possible loss — and
 * the pickers cap none of those lists, so no column width makes this unnecessary.
 */
export function clipDetail(detail: string, maxLength: number = DETAIL_MAX_LENGTH): string {
  if (!detail || detail.length <= maxLength) {
    return detail;
  }

  const segments = detail.split(";");
  // The ';' separators survive the join and count toward the budget.
  let total = segments.reduce((n, s) => n + s.length, 0) + segments.length - 1;

  while (total > maxLength) {
    let longest = 0;
    for (let i = 1; i < segments.length; i++) {
      if (segments[i].length > segments[longest].length) {
        longest = i;
      }
    }

    const segment = segments[longest];
    // Never eat a segment's own marker — a "tags: " with no value still tells the view the field
    // was present and empty, whereas a missing marker reads as a different kind of event.
    const marker = MARKER.exec(segment);
    const floor = marker ? marker[0].length : 0;

    if (segment.length <= floor) {
      break;
    }

    const next = Math.max(floor, segment.length - (total - maxLength));
    total -= segment.length - next;
    segments[longest] = segment.slice(0, next);
  }

  const clipped = segments.join(";");

  // Backstop for the pathological case where every segment is already at its floor.
  return clipped.length > maxLength ? clipped.slice(0, maxLength) : clipped;
}
